import React, { useState, useEffect, useRef } from 'react'
import { MessageCircle, X, Send } from 'lucide-react'
import './ChatSupportWidget.css'
import { askFaqBot } from '../../api/faq'

// Chat response service interface
class ChatResponseService {
	async getResponse(message) {
		throw new Error('Method not implemented')
	}
}

// Simple static response service
class StaticResponseService extends ChatResponseService {
	async getResponse(message) {
		return {
			id: Date.now(),
			text: 'Yes, I am a bot',
			sender: 'bot',
		}
	}
}

// Example of another response service (e.g., API-based)
class ApiResponseService extends ChatResponseService {
	constructor(apiCallBack, lang) {
		super()
		this.lang = lang
		this.apiCallBack = apiCallBack
	}

	async getResponse(message) {
		try {
			// This is just a placeholder for an actual API call
			// In a real implementation, you would make a fetch request to this.apiUrl
			const result = await this.apiCallBack({
				question: message.text,
				lang: this.lang,
			})

			return {
				id: Date.now(),
				text: result.answer,
				sender: 'bot',
			}
		} catch (error) {
			console.error('Error fetching response from API:', error)
			return {
				id: Date.now(),
				text: 'Sorry, I encountered an error processing your request.',
				sender: 'bot',
			}
		}
	}
}

export default function ChatSupportWidget({
	responseService = new ApiResponseService(askFaqBot, 'fr'),
	title = 'Support Chat',
	initialMessage = "Comment pouvons-nous vous aider aujourd'hui ?",
	responseDelay = 500,
}) {
	const [isOpen, setIsOpen] = useState(false)
	const [messages, setMessages] = useState([])
	const [inputValue, setInputValue] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const messagesEndRef = useRef(null)

	const toggleChat = () => {
		setIsOpen(!isOpen)
	}

	const scrollToBottom = () => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}

	// Call scrollToBottom whenever messages change
	useEffect(() => {
		scrollToBottom()
	}, [messages])

	const handleSubmit = async (e) => {
		e.preventDefault()

		if (inputValue.trim() === '') return

		// Add user message to chat history
		const userMessage = {
			id: Date.now(),
			text: inputValue,
			sender: 'user',
		}

		setMessages((prevMessages) => [...prevMessages, userMessage])
		setInputValue('')
		setIsLoading(true)

		try {
			// Add delay for natural feel
			await new Promise((resolve) => setTimeout(resolve, responseDelay))

			// Get response from the current service
			const botResponse = await responseService.getResponse(userMessage)

			setMessages((prevMessages) => [...prevMessages, botResponse])
		} catch (error) {
			console.error('Error getting response:', error)
			const errorResponse = {
				id: Date.now(),
				text: 'Sorry, something went wrong.',
				sender: 'bot',
			}
			setMessages((prevMessages) => [...prevMessages, errorResponse])
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className='chat-widget-container'>
			{/* Chat Icon Button */}
			<button
				onClick={toggleChat}
				className={`chat-toggle-button ${isOpen ? 'close' : ''}`}
			>
				{isOpen ? <X size={24} /> : <MessageCircle size={24} />}
			</button>

			{/* Chat Window */}
			{isOpen && (
				<div className='chat-window'>
					{/* Header */}
					<div className='chat-header'>
						<h3>{title}</h3>
						<button onClick={toggleChat} className='close-button'>
							<X size={18} />
						</button>
					</div>

					{/* Messages Container */}
					<div className='messages-container'>
						{messages.length === 0 ? (
							<div className='empty-message'>
								{initialMessage}
							</div>
						) : (
							messages.map((message) => (
								<div
									key={message.id}
									className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
								>
									{message.text}
								</div>
							))
						)}
						{isLoading && (
							<div className='message bot-message loading'>
								<span className='loading-dot'></span>
								<span className='loading-dot'></span>
								<span className='loading-dot'></span>
							</div>
						)}
						{/* Invisible element to scroll to */}
						<div ref={messagesEndRef} />
					</div>

					{/* Input Form */}
					<form onSubmit={handleSubmit} className='chat-input-form'>
						<input
							type='text'
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							placeholder='Tapez votre message...'
							className='chat-input'
							disabled={isLoading}
						/>
						<button
							type='submit'
							className='send-button'
							disabled={isLoading || inputValue.trim() === ''}
						>
							<Send size={18} />
						</button>
					</form>
				</div>
			)}
		</div>
	)
}

// Export the service classes so they can be used by consumers
export { ChatResponseService, StaticResponseService, ApiResponseService }
